/**
 * User groups management component for admin panel.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Form, Modal, Alert, Spinner, InputGroup, Badge } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import { PAGINATION } from '@/constants';

interface UserGroup {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  roles?: Role[];
}

interface Role {
  id: number;
  name: string;
  description?: string;
}

interface UserGroupDetail {
  user_group: UserGroup;
  roles: Role[];
  user_count: number;
}

interface UserGroupsListResponse {
  user_groups: UserGroup[];
  total: number;
  skip: number;
  limit: number;
}

export default function UserGroupsManagement() {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<UserGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const pageSize = PAGINATION.DEFAULT_PAGE_SIZE;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    loadGroups();
    loadRoles();
  }, [currentPage, searchTerm]);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAdminGroups({
        skip: currentPage * pageSize,
        limit: pageSize,
        search: searchTerm || undefined,
      });

      if (response.error) {
        toast.error(response.error);
      } else {
        const data = response.data as UserGroupsListResponse;
        setGroups(data.user_groups || []);
        setTotalGroups(data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to load user groups');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await apiClient.getAdminRoles();
      if (!response.error && response.data) {
        setRoles((response.data as Role[]) || []);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const loadGroupDetails = async (groupId: number) => {
    try {
      const response = await apiClient.getAdminGroup(groupId);
      if (!response.error && response.data) {
        setSelectedGroup(response.data as UserGroupDetail);
        setShowRolesModal(true);
      }
    } catch (error) {
      toast.error('Failed to load group details');
      console.error(error);
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (group: UserGroup) => {
    setEditingGroup(group);
    setFormData({ name: group.name, description: group.description || '' });
    setShowModal(true);
  };

  const handleDelete = async (groupId: number) => {
    if (!window.confirm('Are you sure you want to delete this user group?')) {
      return;
    }

    try {
      const response = await apiClient.deleteAdminGroup(groupId);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('User group deleted successfully');
        loadGroups();
      }
    } catch (error) {
      toast.error('Failed to delete user group');
      console.error(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingGroup) {
        const response = await apiClient.updateAdminGroup(editingGroup.id, formData);

        if (response.error) {
          toast.error(response.error);
        } else {
          toast.success('User group updated successfully');
          setShowModal(false);
          loadGroups();
        }
      } else {
        const response = await apiClient.createAdminGroup(formData);

        if (response.error) {
          toast.error(response.error);
        } else {
          toast.success('User group created successfully');
          setShowModal(false);
          loadGroups();
        }
      }
    } catch (error) {
      toast.error('Failed to save user group');
      console.error(error);
    }
  };

  const handleToggleRole = async (roleId: number) => {
    if (!selectedGroup) return;

    const hasRole = selectedGroup.roles.some((r) => r.id === roleId);

    try {
      let response;
      if (hasRole) {
        response = await apiClient.removeRoleFromGroup(selectedGroup.user_group.id, roleId);
      } else {
        response = await apiClient.addRoleToGroup(selectedGroup.user_group.id, roleId);
      }

      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success(`Role ${hasRole ? 'removed' : 'added'} successfully`);
        loadGroupDetails(selectedGroup.user_group.id);
      }
    } catch (error) {
      toast.error('Failed to update role');
      console.error(error);
    }
  };

  const totalPages = Math.ceil(totalGroups / pageSize);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>User Groups</h2>
        <Button onClick={handleCreate} variant="primary">
          Create Group
        </Button>
      </div>

      <InputGroup className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search groups by name..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(0);
          }}
        />
      </InputGroup>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : groups.length === 0 ? (
        <Alert variant="info">No user groups found</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Roles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id}>
                  <td>{group.id}</td>
                  <td>{group.name}</td>
                  <td>{group.description || '-'}</td>
                  <td>
                    {group.roles && group.roles.length > 0 ? (
                      <div>
                        {group.roles.map((role) => (
                          <Badge key={role.id} bg="primary" className="me-1 mb-1">
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">No roles</span>
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-info"
                      onClick={() => loadGroupDetails(group.id)}
                      className="me-2"
                    >
                      Manage Roles
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => handleEdit(group)} className="me-2">
                      Edit
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(group.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalGroups)} of{' '}
              {totalGroups} groups
            </div>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="me-2"
              >
                Previous
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? 'Edit User Group' : 'Create User Group'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showRolesModal} onHide={() => setShowRolesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Roles - {selectedGroup?.user_group.name} ({selectedGroup?.user_count || 0} users)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGroup && (
            <div>
              <h5>Assigned Roles</h5>
              {selectedGroup.roles.length === 0 ? (
                <p className="text-muted">No roles assigned</p>
              ) : (
                <div>
                  {selectedGroup.roles.map((role) => (
                    <Badge key={role.id} bg="primary" className="me-2 mb-2" style={{ fontSize: '1rem', padding: '0.5rem' }}>
                      {role.name}
                      <Button
                        variant="link"
                        size="sm"
                        className="text-white ms-2"
                        onClick={() => handleToggleRole(role.id)}
                        style={{ textDecoration: 'none', padding: 0 }}
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}

              <hr />

              <h5>Available Roles</h5>
              <div>
                {roles
                  .filter((role) => !selectedGroup.roles.some((r) => r.id === role.id))
                  .map((role) => (
                    <Badge
                      key={role.id}
                      bg="secondary"
                      className="me-2 mb-2"
                      style={{ fontSize: '1rem', padding: '0.5rem', cursor: 'pointer' }}
                      onClick={() => handleToggleRole(role.id)}
                    >
                      + {role.name}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRolesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
